import type { AdminView, AppTab, AuthTab, User } from "@/types";
import { api } from "@/api";
import { els, state } from "@/state";
import { parseUserRoles } from "@/utils";
import {
  changePage, changePageSize, closeModal, copyApiKey, copyPlainKey,
  assignApiKeyChannel, assignUserChannel, createAdminUser, createApiKey, createBridgeInstance, createChannel, createForwarderInstance, createPricingPlan, createProviderInstance, deletePricingPlan, deleteProviderInstance, editBillingRate, editPricingPlan, fetchAdminApiKey, keyAction, loadAdminData, loadAll,
  createPublicModel,
  exportAdminWallets, markOrderPaid, memberAction, openCreateApiKeyModal, openCreateUserModal, openPolicySettings, openRechargeUserModal, queryAdminWallets, rechargeAdminUser,
  editAdminApiKeyUsageLimits, editAdminUserUsageLimits, reconcileOrderPayment, refreshAdminCurrentView, renderAdminPage, renderAdminUsers, refundOrder, resetAdminUserPassword, revokeApiKey, saveBillingRate, saveBridgeInstance, saveChannel, saveMember, savePricingPlan, saveProviderInstance, setDefaultChannel, deleteChannel,
  savePolicySettings, setAdminChannelTab, setAdminUsersSearch, setKeyCompression, setKeyOptimization, setUserCompression, setUserOptimization,
  setGlobalInteractionCapture, setKeyInteractionCapture, setUserInteractionCapture,
  closeAdminKeyDrawer, closeAdminUserActionMenu, closeTodayErrorRequests, initializeAdminRouteFromLocation, navigateAdminView, openAdminKeyDrawer, openAdminUserDetail, openTodayErrorRequests, returnToAdminUsers, setAdminKeyDrawerTab, setAdminModelUsagePeriod, setAdminUserDetailTab, toggleAdminUserActionMenu,
  openCreateModelModal, openTemporaryChannelOverride,
  revokeChannelOverride, runBridgeInstanceAction, toast, toggleMember, toggleProductStatus, toggleShowAdminKey, updateAdminUserApiKeyStatus
} from "@/views/admin";
import {
  activateEntitlement, editUserApiKeyUsageLimits, fetchUserApiKey, handleAppLogout, handleCopyUserKey, handleDeleteUserKey,
  changeAppUsagePage, changeAppWalletPage, exportAppWallet, handleBuyPricingPlan, handlePayOrder, handleToggleUserKey, initAlipayReturnPage, loadAppDashboardSummary, loadUserApiKeys, openUserApiKeyModal, queryAppWallet, renderAppPage, renderAppTab, setAppModelUsagePeriod,
  refreshAppEntitlements, toggleShowUserKey
} from "@/views/app";
import {
  addChatAttachments, applyChatSuggestion, cancelChatMessage, createChatSession, deleteChatSession, initializeChatCapabilityControls, initializeChatModelControls, initializeChatScrollFollow, loadChatData, removeChatAttachment, renderChatPage, selectChatSession, sendChatMessage
} from "@/views/chat";
import { handleAuthSubmit, handleResendVerification, renderAuthPage, setAuthTab } from "@/views/auth";
import { loadPricingPlans, renderLanding } from "@/views/landing";
import { initVerifyEmailPage } from "@/views/verifyEmail";

/** 分页列表 key 类型 */
type ListKey = "members" | "requestLogs" | "users";

/** 全局 click 事件委托:处理所有 data-action 按钮 */
function handleActionClick(event: MouseEvent): void {
  const target = event.target as HTMLElement;
  const button = target.closest<HTMLElement>("[data-action]");
  if (!button) return;

  const action = button.dataset.action;
  const id = button.dataset.id ?? "";
  if (!action) return;
  if (action !== "toggle-admin-user-menu") closeAdminUserActionMenu();

  switch (action) {
    case "save-member":
      void saveMember(id);
      break;
    case "enable-member":
      void memberAction(id, "enable");
      break;
    case "disable-member":
      void memberAction(id, "disable");
      break;
    case "delete-member":
      void memberAction(id, "delete");
      break;
    case "enable-key":
      void keyAction(id, "enable");
      break;
    case "disable-key":
      void keyAction(id, "disable", "API Key 已停用。");
      break;
    case "delete-key":
      void keyAction(id, "delete", "API Key 已删除。");
      break;
    case "revoke-key":
      void revokeApiKey(id);
      break;
    case "close-modal":
      closeModal();
      break;
    case "copy-plain-key":
      void copyPlainKey();
      break;
    case "copy-key":
      void copyApiKey(id);
      break;
    case "toggle-member":
      toggleMember(id);
      break;
    case "admin-fetch-key":
      void fetchAdminApiKey(id);
      break;
    case "admin-toggle-show-key":
      toggleShowAdminKey(id);
      break;
    case "page-change":
      changePage(
        (button.dataset.list ?? "members") as ListKey,
        button.dataset.page ?? "1"
      );
      break;
    case "open-today-error-requests":
      void openTodayErrorRequests(1);
      break;
    case "change-today-error-page":
      void openTodayErrorRequests(Number(button.dataset.page || "1"));
      break;
    case "close-today-error-requests":
      closeTodayErrorRequests();
      break;
    case "app-logout":
      event.preventDefault();
      void handleAppLogout();
      break;
    case "landing-console":
      event.preventDefault();
      void handleLandingConsole();
      break;
    case "create-user-key":
      openUserApiKeyModal();
      break;
    case "delete-user-key":
      void handleDeleteUserKey(id);
      break;
    case "copy-user-key":
      void handleCopyUserKey(id);
      break;
    case "toggle-user-key":
      void handleToggleUserKey(id, button.dataset.status ?? "");
      break;
    case "edit-user-key-usage-limits":
      void editUserApiKeyUsageLimits(id);
      break;
    case "edit-admin-user-usage-limits":
      void editAdminUserUsageLimits(id);
      break;
    case "edit-admin-key-usage-limits":
      void editAdminApiKeyUsageLimits(id);
      break;
    case "toggle-admin-user-menu":
      event.preventDefault();
      toggleAdminUserActionMenu(button, id);
      break;
    case "open-admin-user-detail":
      event.preventDefault();
      openAdminUserDetail(id);
      break;
    case "return-admin-users":
      returnToAdminUsers();
      break;
    case "set-admin-user-detail-tab":
      setAdminUserDetailTab(button.dataset.tab ?? "usage");
      break;
    case "open-admin-key-drawer":
      openAdminKeyDrawer(id, button.dataset.tab);
      break;
    case "close-admin-key-drawer":
      closeAdminKeyDrawer();
      break;
    case "set-admin-key-drawer-tab":
      setAdminKeyDrawerTab(button.dataset.tab ?? "overview");
      break;
    case "toggle-admin-user-key-status":
      void updateAdminUserApiKeyStatus(id, button.dataset.status === "disabled" ? "disabled" : "active");
      break;
    case "set-global-interaction-capture":
      void setGlobalInteractionCapture(button.dataset.enabled === "true");
      break;
    case "set-user-interaction-capture":
      void setUserInteractionCapture(id, button.dataset.enabled === "true");
      break;
    case "set-key-interaction-capture":
      void setKeyInteractionCapture(id, button.dataset.mode ?? "inherit");
      break;
    case "fetch-key":
      void fetchUserApiKey(id);
      break;
    case "toggle-show-key":
      toggleShowUserKey(id);
      break;
    case "buy-plan":
      void handleBuyPricingPlan(id);
      break;
    case "pay-order":
      void handlePayOrder(id);
      break;
    case "activate-entitlement":
      void activateEntitlement(id);
      break;
    case "refresh-entitlements":
      void refreshAppEntitlements();
      break;
    case "refresh-admin":
      void handleRefreshAdmin();
      break;
    case "query-admin-wallets":
      void queryAdminWallets();
      break;
    case "export-admin-wallets":
      exportAdminWallets();
      break;
    case "query-app-wallet":
      void queryAppWallet();
      break;
    case "export-app-wallet":
      exportAppWallet();
      break;
    case "change-app-wallet-page":
      void changeAppWalletPage(button.dataset.page ?? "1");
      break;
    case "change-app-usage-page":
      void changeAppUsagePage(button.dataset.page ?? "1");
      break;
    case "set-app-model-usage-period":
      setAppModelUsagePeriod(button.dataset.period ?? "total");
      break;
    case "set-admin-model-usage-period":
      setAdminModelUsagePeriod(button.dataset.period ?? "total");
      break;
    case "toggle-user-status":
      void handleToggleUserStatus(id, button.dataset.status ?? "");
      break;
    case "delete-user":
      void handleDeleteUser(id, button.dataset.name ?? "");
      break;
    case "create-admin-user":
      void createAdminUser();
      break;
    case "open-create-user-modal":
      openCreateUserModal();
      break;
    case "create-user-api-key":
      openCreateApiKeyModal(id);
      break;
    case "reset-user-password":
      void resetAdminUserPassword(id, button.dataset.name ?? "");
      break;
    case "recharge-user":
      openRechargeUserModal(id, button.dataset.name ?? "");
      break;
    case "open-policy-settings":
      openPolicySettings((button.dataset.scope ?? "user") as "user" | "key", id);
      break;
    case "set-user-compression":
      void setUserCompression(id, button.dataset.mode ?? "");
      break;
    case "set-key-compression":
      void setKeyCompression(id, button.dataset.mode ?? "");
      break;
    case "set-user-optimization":
      void setUserOptimization(id, parseOptionalBoolean(button.dataset.enabled));
      break;
    case "set-key-optimization":
      void setKeyOptimization(id, parseOptionalBoolean(button.dataset.enabled));
      break;
    case "add-model":
      openCreateModelModal();
      break;
    case "delete-model":
      void handleDeleteModel(id, button.dataset.name ?? "");
      break;
    case "add-account":
      void handleAddAccount();
      break;
    case "create-channel":
      void createChannel();
      break;
    case "set-admin-channel-tab":
      setAdminChannelTab(button.dataset.tab === "instances" ? "instances" : "channels");
      break;
    case "create-bridge-instance":
      void createBridgeInstance();
      break;
    case "create-provider-instance":
      void createProviderInstance();
      break;
    case "create-forwarder-instance":
      void createForwarderInstance();
      break;
    case "bridge-action":
      void runBridgeInstanceAction(id, button.dataset.operation ?? "");
      break;
    case "save-bridge-instance":
      void saveBridgeInstance(id);
      break;
    case "save-provider-instance":
      void saveProviderInstance(id);
      break;
    case "delete-provider-instance":
      void deleteProviderInstance(id, button.dataset.name ?? "");
      break;
    case "save-channel":
      void saveChannel(id);
      break;
    case "delete-channel":
      void deleteChannel(id, button.dataset.name ?? id);
      break;
    case "set-default-channel":
      void setDefaultChannel(id);
      break;
    case "open-temporary-channel-override":
      openTemporaryChannelOverride(button.dataset.subjectType === "api_key" ? "api_key" : "member", id, button.dataset.name || id);
      break;
    case "revoke-channel-override":
      void revokeChannelOverride(id);
      break;
    case "delete-account":
      void handleDeleteAccount(id, button.dataset.name ?? "");
      break;
    case "create-product":
      void createPricingPlan();
      break;
    case "edit-product":
      void editPricingPlan(id);
      break;
    case "edit-billing-rate":
      void editBillingRate(id);
      break;
    case "enable-product":
      void toggleProductStatus(id, true);
      break;
    case "disable-product":
      void toggleProductStatus(id, false);
      break;
    case "delete-product":
      void deletePricingPlan(id, button.dataset.name ?? "");
      break;
    case "mark-order-paid":
      void markOrderPaid(id);
      break;
    case "reconcile-order-payment":
      void reconcileOrderPayment(id);
      break;
    case "refund-order":
      void refundOrder(id);
      break;
    case "chat-new-session":
      void createChatSession();
      break;
    case "chat-select-session":
      void selectChatSession(id);
      break;
    case "chat-delete-session":
      void deleteChatSession(id);
      break;
    case "chat-suggest":
      applyChatSuggestion(button.dataset.prompt ?? "");
      break;
    case "chat-remove-attachment":
      removeChatAttachment(Number(id));
      break;
    default:
      break;
  }
}

function parseOptionalBoolean(value: string | undefined): boolean | null {
  if (value === "true") return true;
  if (value === "false") return false;
  return null;
}

/** 刷新 admin 扩展数据(用户列表 / 健康 / OAuth) */
async function handleRefreshAdmin(): Promise<void> {
  await refreshAdminCurrentView();
  toast("已刷新");
}

async function handleLandingConsole(): Promise<void> {
  await loadCurrentUser();
  if (!state.user) {
    window.location.assign("/login/");
    return;
  }

  const roles = parseUserRoles(state.user);
  window.location.assign(roles.includes("admin") ? "/console/admin/" : "/app/");
}

/** 切换用户启用/停用状态 */
async function handleToggleUserStatus(userId: string, status: string): Promise<void> {
  try {
    const data = await api<{ user: User }>(`/api/admin/users/${encodeURIComponent(userId)}/status`, {
      method: "PATCH",
      body: JSON.stringify({ status })
    });
    state.adminUsers = state.adminUsers.map((user) =>
      user.id === userId ? { ...user, ...data.user } : user
    );
    toast(`用户已${status === "active" ? "启用" : "停用"}`, "success");
    renderAdminUsers();
  } catch (error) {
    toast((error as Error).message, "error");
  }
}

/** 删除用户(软删除 + 级联停用 API Key) */
async function handleDeleteUser(userId: string, userName: string): Promise<void> {
  if (!confirm(`确定要删除用户 "${userName}" 吗?\n\n删除后:\n• 用户无法再登录\n• 其所有 API Key 将被停用\n• 历史请求日志保留用于审计\n\n此操作不可恢复。`)) {
    return;
  }
  try {
    await api(`/api/admin/users/${encodeURIComponent(userId)}/status`, {
      method: "PATCH",
      body: JSON.stringify({ status: "deleted" })
    });
    state.adminUsers = state.adminUsers.filter((user) => user.id !== userId);
    toast(`用户 "${userName}" 已删除`, "success");
    renderAdminUsers();
  } catch (error) {
    toast((error as Error).message, "error");
  }
}

/** 删除公共模型 */
async function handleDeleteModel(modelId: string, modelName: string): Promise<void> {
  if (!confirm(`确定要删除模型 "${modelName}" 吗?\n删除后通过该模型的请求将失败。`)) {
    return;
  }
  try {
    await api(`/api/admin/models/${encodeURIComponent(modelId)}`, { method: "DELETE" });
    toast(`模型 "${modelName}" 已删除`, "success");
    await refreshAdminCurrentView();
  } catch (error) {
    toast((error as Error).message, "error");
  }
}

/** 添加 ChatGPT 账号 */
async function handleAddAccount(): Promise<void> {
  const labelInput = document.querySelector<HTMLInputElement>("#account-label-input");
  const authJsonInput = document.querySelector<HTMLTextAreaElement>("#account-authjson-input");
  if (!authJsonInput) return;
  const authJson = authJsonInput.value.trim();
  if (!authJson) {
    toast("请粘贴 auth.json 内容", "error");
    return;
  }
  try {
    await api("/api/admin/accounts", {
      method: "POST",
      body: JSON.stringify({ label: labelInput?.value.trim() || "", authJson })
    });
    toast("账号已添加", "success");
    if (labelInput) labelInput.value = "";
    authJsonInput.value = "";
    await refreshAdminCurrentView();
  } catch (error) {
    toast((error as Error).message, "error");
  }
}

/** 删除 ChatGPT 账号 */
async function handleDeleteAccount(accountId: string, label: string): Promise<void> {
  if (!confirm(`确定要删除账号 "${label}" 吗?`)) {
    return;
  }
  try {
    await api(`/api/admin/accounts/${encodeURIComponent(accountId)}`, { method: "DELETE" });
    toast(`账号 "${label}" 已删除`, "success");
    await refreshAdminCurrentView();
  } catch (error) {
    toast((error as Error).message, "error");
  }
}

/** 分页大小变更 */
function handlePaginationChange(event: Event): void {
  const channelSelect = (event.target as HTMLElement).closest<HTMLSelectElement>(
    "select[data-channel-assignment]"
  );
  if (channelSelect) {
    const id = channelSelect.dataset.id || "";
    if (channelSelect.dataset.channelAssignment === "user") {
      void assignUserChannel(id, channelSelect.value);
    } else if (channelSelect.dataset.channelAssignment === "api-key") {
      void assignApiKeyChannel(id, channelSelect.value);
    }
    return;
  }
  const select = (event.target as HTMLElement).closest<HTMLSelectElement>(
    "select[data-pagination-size]"
  );
  if (!select) return;
  const listKey = (select.dataset.paginationSize ?? "members") as ListKey;
  changePageSize(listKey, select.value);
}

/** 用户管理搜索框输入(事件委托,搜索框动态渲染) */
function handleAdminUsersSearchInput(event: Event): void {
  const input = (event.target as HTMLElement).closest<HTMLInputElement>("#adminUsersSearch");
  if (!input) return;
  setAdminUsersSearch(input.value);
}

function handleUserActionMenuDismiss(event: MouseEvent): void {
  const target = event.target as HTMLElement;
  if (target.closest("#adminUserActionMenu") || target.closest('[data-action="toggle-admin-user-menu"]')) return;
  closeAdminUserActionMenu();
}

/** Escape 键关闭弹窗 */
function handleKeydown(event: KeyboardEvent): void {
  if (event.key !== "Escape") return;
  if (closeAdminUserActionMenu()) return;
  if (els.modalBackdrop && !els.modalBackdrop.classList.contains("hidden")) {
    closeModal();
    return;
  }
  if (state.adminKeyDrawer) closeAdminKeyDrawer();
}

/** admin 侧边栏导航点击 */
function handleNavItemClick(event: MouseEvent): void {
  const item = event.currentTarget as HTMLElement;
  const view = item.dataset.view as AdminView | undefined;
  if (!view) return;
  navigateAdminView(view);
  void refreshAdminCurrentView().catch((error) => toast((error as Error).message, "error"));
}

function handleAdminPopState(): void {
  if (document.body.dataset.page !== "admin") return;
  initializeAdminRouteFromLocation();
  renderAdminPage();
  void refreshAdminCurrentView().catch((error) => toast((error as Error).message, "error"));
}

/** app 标签切换 */
function handleAppTabClick(event: MouseEvent): void {
  const btn = event.currentTarget as HTMLElement;
  const tab = btn.dataset.appTab as AppTab | undefined;
  if (!tab) return;
  renderAppTab(tab);
}

/** auth 登录/注册标签切换 */
function handleAuthTabClick(event: MouseEvent): void {
  const btn = event.currentTarget as HTMLElement;
  const tab = btn.dataset.authTab as AuthTab | undefined;
  if (!tab) return;
  setAuthTab(tab);
}

/** 绑定所有全局事件 */
function bindEvents(): void {
  // 全局 click 委托:data-action 按钮
  document.addEventListener("click", handleActionClick);
  document.addEventListener("click", handleUserActionMenuDismiss);

  // 分页大小变更
  document.addEventListener("change", handlePaginationChange);

  // 用户管理搜索框输入(事件委托)
  document.addEventListener("input", handleAdminUsersSearchInput);

  // Escape 关闭弹窗
  document.addEventListener("keydown", handleKeydown);
  window.addEventListener("popstate", handleAdminPopState);
  window.addEventListener("resize", closeAdminUserActionMenu);
  document.addEventListener("scroll", closeAdminUserActionMenu, true);

  // modalBackdrop 点击关闭
  if (els.modalBackdrop) {
    els.modalBackdrop.addEventListener("click", (event) => {
      if (event.target === els.modalBackdrop) closeModal();
    });
  }

  // admin 侧边栏导航
  els.navItems.forEach((item) => {
    item.addEventListener("click", handleNavItemClick);
  });

  // admin 按钮
  els.apiKeyForm?.addEventListener("submit", createApiKey);
  els.createUserForm?.addEventListener("submit", (event) => {
    event.preventDefault();
    void createAdminUser();
  });
  els.rechargeUserForm?.addEventListener("submit", (event) => {
    event.preventDefault();
    void rechargeAdminUser();
  });
  els.billingRateForm?.addEventListener("submit", (event) => {
    void saveBillingRate(event);
  });
  els.pricingPlanForm?.addEventListener("submit", (event) => {
    void savePricingPlan(event);
  });
  els.createModelForm?.addEventListener("submit", (event) => {
    void createPublicModel(event);
  });
  els.policySettingsForm?.addEventListener("submit", (event) => {
    void savePolicySettings(event);
  });
  els.refreshButton?.addEventListener("click", () => {
    refreshAdminCurrentView().catch((error) => toast((error as Error).message));
  });
  els.refreshLogsButton?.addEventListener("click", () => {
    refreshAdminCurrentView().catch((error) => toast((error as Error).message));
  });

  // auth 登录/注册标签
  document.querySelectorAll<HTMLElement>("[data-auth-tab]").forEach((btn) => {
    btn.addEventListener("click", handleAuthTabClick);
  });
  // auth 表单提交
  els.authForm?.addEventListener("submit", handleAuthSubmit);
  document.querySelector<HTMLButtonElement>("#authResendBtn")?.addEventListener("click", () => {
    void handleResendVerification();
  });

  // app 标签
  document.querySelectorAll<HTMLElement>("[data-app-tab]").forEach((btn) => {
    btn.addEventListener("click", handleAppTabClick);
  });

  // chat 输入框:Enter 发送,Shift+Enter 换行
  const chatInput = document.querySelector<HTMLTextAreaElement>("#chatInput");
  if (chatInput) {
    chatInput.addEventListener("keydown", (event: KeyboardEvent) => {
      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        void sendChatMessage();
      }
    });
  }
  const chatSendBtn = document.querySelector<HTMLButtonElement>("#chatSendBtn");
  if (chatSendBtn) {
    chatSendBtn.addEventListener("click", () => {
      void sendChatMessage();
    });
  }
  const chatCancelBtn = document.querySelector<HTMLButtonElement>("#chatCancelBtn");
  if (chatCancelBtn) {
    chatCancelBtn.addEventListener("click", cancelChatMessage);
  }
  const chatAttachmentButton = document.querySelector<HTMLButtonElement>("#chatAttachmentButton");
  const chatAttachmentInput = document.querySelector<HTMLInputElement>("#chatAttachmentInput");
  if (chatAttachmentButton && chatAttachmentInput) {
    chatAttachmentButton.addEventListener("click", () => chatAttachmentInput.click());
    chatAttachmentInput.addEventListener("change", () => {
      if (chatAttachmentInput.files) void addChatAttachments(chatAttachmentInput.files);
      chatAttachmentInput.value = "";
    });
  }
  if (chatInput) {
    chatInput.addEventListener("paste", (event: ClipboardEvent) => {
      const imageFiles = Array.from(event.clipboardData?.files || []).filter((file) => file.type.startsWith("image/"));
      if (imageFiles.length > 0) {
        event.preventDefault();
        void addChatAttachments(imageFiles);
      }
    });
  }
  initializeChatCapabilityControls();
  initializeChatModelControls();
  initializeChatScrollFollow();
}

async function loadCurrentUser(): Promise<void> {
  try {
    const meData = await api<{ member: User | null; csrfToken?: string }>("/api/auth/me");
    state.user = meData.member || null;
    state.csrfToken = meData.csrfToken || "";
  } catch {
    state.user = null;
    state.csrfToken = "";
  }
}

async function requireUser(): Promise<User> {
  await loadCurrentUser();
  if (state.user) {
    return state.user;
  }
  window.location.href = "/login/";
  throw new Error("未登录");
}

function resolveInitialAppTab(): AppTab {
  const tab = new URLSearchParams(window.location.search).get("tab");
  const allowedTabs: AppTab[] = ["overview", "keys", "entitlements", "wallet", "orders", "usage", "modelUsage", "settings", "docs"];
  return tab && allowedTabs.includes(tab as AppTab) ? (tab as AppTab) : "overview";
}

async function initHomePage(): Promise<void> {
  await loadCurrentUser();
  await loadPricingPlans();
  renderLanding();
}

function initAuthPage(): void {
  state.route = location.pathname.includes("register") ? "register" : "login";
  renderAuthPage();
}

async function initVerifyEmailResultPage(): Promise<void> {
  state.route = "verifyEmail";
  await initVerifyEmailPage();
}

async function initAppPage(): Promise<void> {
  await requireUser();
  state.appTab = resolveInitialAppTab();
  await Promise.all([loadUserApiKeys(), loadAppDashboardSummary()]);
  renderAppPage();
}

async function initAdminPage(): Promise<void> {
  const user = await requireUser();
  if (!parseUserRoles(user).includes("admin")) {
    window.location.href = "/app/";
    return;
  }
  initializeAdminRouteFromLocation();
  await loadAll();
  await loadAdminData();
  renderAdminPage();
}

async function initChatPage(): Promise<void> {
  await requireUser();
  await loadChatData();
  renderChatPage();
}

/** 初始化:根据当前 HTML 页面启动对应入口,不做前端全局路由分发。 */
async function init(): Promise<void> {
  bindEvents();

  const page = document.body.dataset.page || "home";
  if (page === "alipay-return") {
    await initAlipayReturnPage();
    return;
  }
  if (page === "auth") {
    initAuthPage();
    return;
  }
  if (page === "verify-email") {
    await initVerifyEmailResultPage();
    return;
  }
  if (page === "app") {
    await initAppPage();
    return;
  }
  if (page === "admin") {
    await initAdminPage();
    return;
  }
  if (page === "chat") {
    await initChatPage();
    return;
  }

  await initHomePage();
}

init().catch((error) => toast((error as Error).message));
